import React, { useState, useRef, useEffect, ReactNode } from 'react';
import '../css/actionButton.css';
import { MoreIcon, MoreHorizontalIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter'

export type ActionButtonItem = {
  id: string;
  label: string;
  icon: ReactNode;
  onClick?: () => void;
};

interface ActionButtonProps {
  items: ActionButtonItem[];
  state?: "default" | "hover" | "active";
  iconVariant?: 'vertical' | 'horizontal';
}

const ActionButton: React.FC<ActionButtonProps> = ({ items, state, iconVariant = 'vertical' }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isControlled = state !== undefined;
  const isDisabled = isControlled;

  const IconComponent = iconVariant === 'horizontal' ? MoreHorizontalIcon : MoreIcon;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getButtonClass = () => {
    if (state === "hover") return "scl--cms-components-actionButton hover";
    if (state === "active") return "scl--cms-components-actionButton active";
    return `scl--cms-components-actionButton ${open ? "active" : ""}`;
  };

  return (
    <div className="scl--cms-components-actionButton-wrapper" ref={wrapperRef}>
      <button
        className={getButtonClass()}
        onClick={() => !isDisabled && setOpen((prev) => !prev)}
        disabled={isDisabled}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <IconComponent className="scl--cms-components-actionButton-icon" />
      </button>

      <div
        className={`scl--cms-components-actionButton-popup ${
          !isControlled && open ? "scl--cms-components-actionButton-popup-open" : ""
        }`}
        role="menu"
      >
        {items.map((item) => (
          <button
            key={item.id}
            className="scl--cms-components-actionButton-popup-item"
            onClick={() => {
              /* istanbul ignore next */
              if (isControlled) /* istanbul ignore next */ return;
              // Fire item handler first, then close menu
              try {
                item.onClick?.();
              } finally {
                setOpen(false);
              }
            }}
            disabled={isControlled}
            role="menuitem"
          >
            {item.icon}
            <span>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ActionButton;